import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import clinicService from '../../../../../services/clinic.service'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'

function LinksCard({ linkitem, getLinks }) {
    const [edit, setEdit] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        defaultValues: {
            id: linkitem.id,
            linkType: linkitem.linkType,
            clinicId: linkitem.clinicId,
            link: linkitem.link,
            notes: linkitem.notes,
            extraData: linkitem.extraData,
            isActive: linkitem.isActive.toString()
        }
    })

    const onSubmit = (data) => {
        setIsSubmitting(true)
        clinicService.addUpdateReputationLink(linkitem.clinicId, data)
            .then(res => {
                toast.success("Link updated successfully!", {
                    position: toast.POSITION.BOTTOM_RIGHT
                })
                setEdit(false)
                getLinks()
            })
            .catch(err => {
                console.log(err)
                toast.error("Error updating link. Please try again.", {
                    position: toast.POSITION.BOTTOM_RIGHT
                })
            })
            .finally(() => {
                setIsSubmitting(false)
            })
    }

    const handleDelete = (clinicId, id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                confirmButton: `btn btn-success btn-sm me-2`,
                cancelButton: 'btn btn-danger btn-sm me-2',
            },
            buttonsStyling: false
        }).then((result) => {
            if (result.isConfirmed) {
                clinicService.deleteReputationLink(clinicId, id)
                    .then(res => {
                        getLinks()
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Link has been deleted successfully.',
                            icon: 'success',
                            confirmButtonColor: '#3085d6',
                            customClass: {
                                confirmButton: 'btn btn-success'
                            },
                            buttonsStyling: false
                        })
                    })
                    .catch(err => {
                        Swal.fire({
                            title: 'Error!',
                            text: 'Unable to delete link. Please try again.',
                            icon: 'error',
                            confirmButtonColor: '#3085d6',
                            customClass: {
                                confirmButton: 'btn btn-primary'
                            },
                            buttonsStyling: false
                        })
                    })
            }
        })
    }

    const getPlatformIcon = (linkType) => {
        switch (linkType) {
            case 'FACEBOOK': return 'bi-facebook text-primary'
            case 'INSTAGRAM': return 'bi-instagram text-danger'
            case 'GOOGLE': return 'bi-google text-success'
            case 'YELP': return 'bi-star-fill text-warning'
            case 'WEBSITE': return 'bi-globe text-info'
            default: return 'bi-link-45deg text-secondary'
        }
    }

    const cancelEdit = () => {
        setEdit(false)
        reset()
    }

    return (
        <div className="col-md-12 col-lg-12 mb-4">
            <div className="card card-padding h-100 card-border shadow-none">
                <div className="bg-transparent d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <i className={`bi ${getPlatformIcon(linkitem.linkType)} me-2 fs-5`}></i>
                        <h6 className="card-title mb-0 text-capitalize">{linkitem.linkType.toLowerCase()}</h6>
                    </div>
                    <div className="btn-group">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setEdit(!edit)}
                            title={edit ? "Cancel" : "Edit"}
                        >
                            <i className={`bi ${edit ? "bi-x-lg" : "bi-pencil"}`}></i>
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    {!edit ? (
                        <>
                            <div className="mb-3">
                                <Link
                                    to={linkitem.link}
                                    className="text-truncate d-block text-primary text-decoration-none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={linkitem.link}
                                >
                                    <i className="bi bi-link-45deg me-1"></i>
                                    {linkitem.link}
                                </Link>
                            </div>

                            {linkitem.notes && (
                                <div className="mb-2">
                                    <small className="text-muted d-block">
                                        <i className="bi bi-chat-text me-1"></i>
                                        <strong>Notes:</strong> {linkitem.notes}
                                    </small>
                                </div>
                            )}

                            {linkitem.extraData && (
                                <div className="mb-2">
                                    <small className="text-muted d-block">
                                        <i className="bi bi-plus-circle me-1"></i>
                                        <strong>Extra Data:</strong> {linkitem.extraData}
                                    </small>
                                </div>
                            )}

                            <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top">
                                <span className={`badge service-status ${linkitem.isActive === true ? 'status-completed text-success' : 'status-incomplete text-danger'}`}>
                                    <i className={`bi ${linkitem.isActive === true ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                                    {linkitem.isActive === true ? "Active" : "Inactive"}
                                </span>

                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDelete(linkitem.clinicId, linkitem.id)}
                                    title="Delete link"
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </div>
                        </>
                    ) : (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="mb-3">
                                <label className="form-label small fw-semibold">URL</label>
                                <input
                                    type="text"
                                    className={`form-control form-control-sm ${errors.link ? 'is-invalid' : ''}`}
                                    {...register("link", { required: "URL is required" })}
                                />
                                {errors.link && (
                                    <div className="invalid-feedback small">
                                        {errors.link.message}
                                    </div>
                                )}
                                <input type="hidden" {...register("id")} />
                                <input type="hidden" {...register("clinicId")} />
                                <input type="hidden" {...register('linkType')} />
                            </div>

                            <div className="mb-3">
                                <label className="form-label small fw-semibold">Notes</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    {...register("notes")}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label small fw-semibold">Extra Data</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    {...register("extraData")}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label small fw-semibold">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    {...register("isActive")}
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>

                            <div className="d-flex justify-content-between gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm flex-fill"
                                    onClick={cancelEdit}
                                    disabled={isSubmitting}
                                >
                                    <i className="bi bi-x-circle me-1"></i> Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-sm flex-fill"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check-circle me-1"></i> Update
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    )
}

export default LinksCard